import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

// Draw counting lines, zones, TTC/PET zones and lane boundaries on a camera
// frame and save them to the board.
//
// Counting lines and zones go to lines.json; their points are kept in the
// photo's own pixels and saved with sourceWidth / sourceHeight, which the AI
// scales from. TTC/PET zones and lanes go to linesttc.json, which the AI reads
// in a fixed 1920x1080 frame (or the config's coord_space, where a board states
// one), so those points are converted to that space. The AI reads both files
// only at start, so every save is followed by a camera restart request.

type Mode = "line" | "zone" | "ttc" | null;
type Pt = [number, number];

interface Props {
  url: string;
  tunnelUrl?: string;
  userName?: string;
  // The image is shown whole (contain) while editing; the frame reports its size.
  frameW: number;
  frameH: number;
  naturalW: number;
  naturalH: number;
  active: boolean;
  setActive: (on: boolean) => void;
}

const base = (t?: string) => String(t || "").replace(/\/+$/, "");
const r2 = (v: number) => Math.round(v * 10) / 10;

const nextName = (items: any[], type: "line" | "zone") => {
  const names = items
    .map((it) => (type === "line" ? it?.type === "line" && it?.line_name : it?.type !== "line" && it?.region_name))
    .filter(Boolean) as string[];
  const nums = names.map((n) => Number((n.match(/\d+/) || ["0"])[0]));
  return `${type}${(nums.length ? Math.max(...nums) : 0) + 1}`;
};

const nextTtcName = (items: any[]) => {
  const nums = items
    .filter((it) => it?.type === "region")
    .map((it) => Number((String(it.region_name || "").match(/\d+/) || ["-1"])[0]));
  return `zone${(nums.length ? Math.max(...nums) : -1) + 1}_ttc`;
};

// The frame size TTC coordinates are written in.
const ttcSpace = (cfg: any): { w: number; h: number } => {
  const m = /(\d+)\s*x\s*(\d+)/.exec(String(cfg?.coord_space || ""));
  return m ? { w: Number(m[1]), h: Number(m[2]) } : { w: 1920, h: 1080 };
};

// A TTC zone is drawn the way the board's zone_editor.py draws it: 4 corners
// (near-left, near-right, far-right, far-left), then the lane lines inside
// that zone, 2 clicks each. Zone and lanes are saved together.
const MAX_LANES = 6;
const NEED: Record<Exclude<Mode, null>, number> = { line: 2, zone: 4, ttc: 4 };
const CORNERS = ["near-left", "near-right", "far-right", "far-left"];
const hintFor = (mode: Exclude<Mode, null>, n: number) => {
  if (mode === "line") return `Click 2 points for the line (${n}/2)`;
  if (mode === "zone") return `Click 4 corners for the zone (${n}/4)`;
  if (n < 4) return `Zone corner ${n + 1} of 4: ${CORNERS[n]}`;
  const lanes = Math.floor((n - 4) / 2);
  return (n - 4) % 2
    ? `Lane line ${lanes + 1}: click its other end`
    : `${lanes} lane line(s) drawn. Click 2 points for another lane line inside the zone, or Save`;
};

const LineZoneEditor: React.FC<Props> = ({ tunnelUrl, userName, frameW, frameH, naturalW, naturalH, active, setActive }) => {
  const [mode, setMode] = useState<Mode>(null);
  const [pts, setPts] = useState<Pt[]>([]);
  const [name, setName] = useState("");
  const [dist, setDist] = useState("");
  const [config, setConfig] = useState<any>(null);
  const [ttc, setTtc] = useState<any>(null);
  const [status, setStatus] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // contain-fit of the photo inside the frame
  const fit = useMemo(() => {
    if (!frameW || !frameH || !naturalW || !naturalH) return null;
    const s = Math.min(frameW / naturalW, frameH / naturalH);
    return { s, ox: (frameW - naturalW * s) / 2, oy: (frameH - naturalH * s) / 2 };
  }, [frameW, frameH, naturalW, naturalH]);

  const loadConfig = useCallback(async () => {
    if (!tunnelUrl) return;
    try {
      const { data } = await axios.get(`${base(tunnelUrl)}/get-lines`, { timeout: 15000 });
      setConfig(data && typeof data === "object" ? data : { items: [] });
    } catch (e) {
      setConfig(null);
      setStatus("Could not reach the board, so its lines are not shown and saving will fail until it is online.");
    }
    try {
      const { data } = await axios.get(`${base(tunnelUrl)}/get-lines-ttc`, { timeout: 15000 });
      setTtc(data && typeof data === "object" ? data : { items: [], lane_boundaries: [] });
    } catch (e) {
      setTtc(null);
    }
  }, [tunnelUrl]);

  useEffect(() => {
    setMode(null);
    setPts([]);
    setStatus("");
    if (active) loadConfig();
  }, [active, loadConfig]);

  const items: any[] = config?.items || [];
  const ttcItems: any[] = ttc?.items || [];
  const lanes: any[] = ttc?.lane_boundaries || [];
  const need = mode ? NEED[mode] : 0;
  const isTtc = mode === "ttc";
  // Clicks still accepted: a TTC zone takes lane lines after its 4 corners.
  const maxPts = mode === "ttc" ? 4 + 2 * MAX_LANES : need;
  const ready = !!mode && (mode === "ttc" ? pts.length >= 4 && (pts.length - 4) % 2 === 0 : pts.length === need);

  const startMode = (m: Exclude<Mode, null>) => {
    setMode(m);
    setPts([]);
    setName(m === "ttc" ? nextTtcName(ttcItems) : nextName(items, m));
    setDist(String((m === "ttc" ? ttc?.default_distance_m : config?.default_distance_m) ?? ""));
    if (config) setStatus("");
  };

  const onClick = (e: React.MouseEvent) => {
    if (!mode || !fit || pts.length >= maxPts) return;
    const r = svgRef.current!.getBoundingClientRect();
    const x = (e.clientX - r.left - fit.ox) / fit.s;
    const y = (e.clientY - r.top - fit.oy) / fit.s;
    if (x < 0 || y < 0 || x > naturalW || y > naturalH) return;
    setPts((p) => [...p, [Math.round(x * 100) / 100, Math.round(y * 100) / 100]]);
  };

  const toScreen = (p: Pt) => (fit ? [fit.ox + p[0] * fit.s, fit.oy + p[1] * fit.s] : [0, 0]);
  const scaleOf = (it: any) => ({
    sw: Number(it?.sourceWidth) || 1920,
    sh: Number(it?.sourceHeight) || 1080,
  });
  // existing lines.json items, drawn in the photo's pixel space
  const existing = items.map((it, i) => {
    const { sw, sh } = scaleOf(it);
    const k = (x: number, y: number): Pt => [(x / sw) * naturalW, (y / sh) * naturalH];
    if (it?.type === "line") return { name: it.line_name, pts: [k(it.lx1, it.ly1), k(it.lx2, it.ly2)], line: true, i };
    // Older zones are two lines (l1/l2) instead of a point list.
    if (it?.l1x1 != null && it?.l2x1 != null) {
      return {
        name: it.region_name,
        pts: [k(it.l1x1, it.l1y1), k(it.l1x2, it.l1y2), k(it.l2x2, it.l2y2), k(it.l2x1, it.l2y1)],
        line: false,
        i,
      };
    }
    if (Array.isArray(it?.points)) {
      return {
        name: it.region_name,
        pts: it.points.map((p: any) => (Array.isArray(p) ? k(p[0], p[1]) : k(p.x, p.y))),
        line: false,
        i,
      };
    }
    return null;
  }).filter(Boolean) as { name: string; pts: Pt[]; line: boolean; i: number }[];

  // existing TTC zones and lanes (linesttc.json), in the photo's pixel space
  const sp = ttcSpace(ttc);
  const kt = (x: number, y: number): Pt => [(Number(x) / sp.w) * naturalW, (Number(y) / sp.h) * naturalH];
  const ttcZones = ttcItems
    .filter((it) => it?.type === "region" && it?.l1x1 != null && it?.l2x1 != null)
    .map((it) => ({
      name: `${it.region_name}${it.dist_m != null ? ` (${it.dist_m}m)` : ""}`,
      pts: [kt(it.l1x1, it.l1y1), kt(it.l1x2, it.l1y2), kt(it.l2x2, it.l2y2), kt(it.l2x1, it.l2y1)],
    }));
  const ttcLanes = lanes
    .filter((l) => Array.isArray(l?.pt1) && Array.isArray(l?.pt2))
    .map((l) => [kt(l.pt1[0], l.pt1[1]), kt(l.pt2[0], l.pt2[1])]);

  // The AI reads both configs only at start. The board restarts the camera
  // only if one of them changed since the camera last started, at most once a minute.
  const requestRestart = async (what: string) => {
    try {
      const { data: r } = await axios.post(`${base(tunnelUrl)}/restart-camera`, {}, { timeout: 20000 });
      setStatus(
        r?.restarted
          ? `Saved and camera restarted at ${dayjs().format("h:mm A")}. New ${what} counts from now.`
          : "Saved. The camera already uses this configuration."
      );
    } catch (e: any) {
      const code = e?.response?.status;
      setStatus(
        code === 429
          ? "Saved, but the camera was restarted less than a minute ago. Save again after a minute to apply this change."
          : code === 404 || code === 405
            ? `Saved. This board has no restart option yet, so the new ${what} is used after the next camera restart.`
            : `Saved, but the camera restart request failed. The new ${what} is used after the next camera restart.`
      );
    }
  };

  const saveLines = async (now: string) => {
    const common = { dist_m: Number(dist) || undefined, sourceWidth: naturalW, sourceHeight: naturalH, created_at: now, created_by: userName || "" };
    const item =
      mode === "line"
        ? { type: "line", line_name: name.trim(), lx1: pts[0][0], ly1: pts[0][1], lx2: pts[1][0], ly2: pts[1][1], ...common }
        : { type: "zone_occupancy", region_name: name.trim(), points: pts.map(([x, y]) => ({ x, y })), ...common };
    // Re-read right before writing so a change made elsewhere is not lost.
    const { data: fresh } = await axios.get(`${base(tunnelUrl)}/get-lines`, { timeout: 15000 });
    const cur = fresh && typeof fresh === "object" ? fresh : { items: [] };
    setConfig(cur);
    if ((cur.items || []).some((it: any) => (it.line_name || it.region_name) === name.trim())) {
      setStatus(`"${name.trim()}" already exists on the board, pick another name.`);
      return false;
    }
    const payload = { ...cur, items: [...(cur.items || []), item], updated_at: now, updated_by: userName || "" };
    await axios.post(`${base(tunnelUrl)}/save-lines`, payload, { timeout: 20000 });
    setConfig(payload);
    return true;
  };

  const saveTtc = async (now: string) => {
    const { data: fresh } = await axios.get(`${base(tunnelUrl)}/get-lines-ttc`, { timeout: 15000 });
    const cur = fresh && typeof fresh === "object" ? fresh : { items: [], lane_boundaries: [] };
    setTtc(cur);
    const s = ttcSpace(cur);
    const t = (p: Pt): Pt => [r2((p[0] / naturalW) * s.w), r2((p[1] / naturalH) * s.h)];
    if ((cur.items || []).some((it: any) => it.region_name === name.trim())) {
      setStatus(`"${name.trim()}" already exists on the board, pick another name.`);
      return false;
    }
    const q = pts.map(t);
    // corners: near-left, near-right, far-right, far-left; l1 = near edge, l2 = far edge
    const [nl, nr, fr, fl] = q;
    const zone = {
      type: "region",
      region_name: name.trim(),
      dist_m: Number(dist) || cur.default_distance_m || 8,
      l1x1: nl[0], l1y1: nl[1], l1x2: nr[0], l1y2: nr[1],
      l2x1: fl[0], l2y1: fl[1], l2x2: fr[0], l2y2: fr[1],
      created_at: now,
      created_by: userName || "",
    };
    const newLanes = [];
    for (let i = 4; i + 1 < q.length; i += 2) newLanes.push({ pt1: q[i], pt2: q[i + 1] });
    const payload = {
      ...cur,
      items: [...(cur.items || []), zone],
      lane_boundaries: [...(cur.lane_boundaries || []), ...newLanes],
      updated_at: now,
      updated_by: userName || "",
    };
    try {
      await axios.post(`${base(tunnelUrl)}/save-lines-ttc`, payload, { timeout: 20000 });
    } catch (e: any) {
      const code = e?.response?.status;
      if (code === 404 || code === 405) {
        setStatus("This board cannot save TTC zones yet (no /save-lines-ttc on it).");
        return false;
      }
      throw e;
    }
    setTtc(payload);
    return true;
  };

  const save = async () => {
    if (!tunnelUrl || !mode || !ready || !name.trim()) return;
    setBusy(true);
    setStatus("Saving…");
    const now = new Date().toISOString();
    const what = mode === "line" ? "line" : mode === "zone" ? "zone" : "TTC zone";
    try {
      const ok = isTtc ? await saveTtc(now) : await saveLines(now);
      if (!ok) return;
      setMode(null);
      setPts([]);
      setStatus("Saved. Restarting the camera service…");
      await requestRestart(what);
    } catch (e) {
      setStatus("Could not save to the board. Is it online?");
    } finally {
      setBusy(false);
    }
  };

  const btn = (on: boolean): React.CSSProperties => ({
    height: 28,
    padding: "0 10px",
    borderRadius: 6,
    border: `1px solid ${on ? "#ED6926" : "#d1d5db"}`,
    background: on ? "#ED6926" : "#fff",
    color: on ? "#fff" : "#1f2937",
    fontSize: 12,
    cursor: "pointer",
  });

  const fmt = (v: any) => (v ? dayjs(v).format("D MMM YYYY, h:mm A") : null);
  const updated = fmt(config?.updated_at);
  const ttcUpdated = fmt(ttc?.updated_at);
  const label = (x: number, y: number, text: string, color: string) => (
    <text x={x + 4} y={y - 4} fill={color} fontSize={12} fontWeight={600} style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 3 }}>
      {text}
    </text>
  );

  return (
    <>
      {active && fit && (
        <svg
          ref={svgRef}
          onClick={onClick}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: mode && pts.length < need ? "crosshair" : "default" }}
        >
          {ttcZones.map((z, i) => {
            const s2 = z.pts.map(toScreen);
            return (
              <g key={`ttc-${i}`}>
                <polygon points={s2.map((p) => p.join(",")).join(" ")} fill="rgba(163,230,53,0.12)" stroke="#a3e635" strokeWidth={2} />
                {label(s2[0][0], s2[0][1], z.name, "#a3e635")}
              </g>
            );
          })}
          {ttcLanes.map((l, i) => {
            const s2 = l.map(toScreen);
            return <line key={`lane-${i}`} x1={s2[0][0]} y1={s2[0][1]} x2={s2[1][0]} y2={s2[1][1]} stroke="#facc15" strokeWidth={2} strokeDasharray="6 5" />;
          })}
          {existing.map((x) => {
            const s2 = x.pts.map(toScreen);
            const d = s2.map((p) => p.join(",")).join(" ");
            return (
              <g key={`ex-${x.i}`}>
                {x.line ? (
                  <polyline points={d} fill="none" stroke="#38bdf8" strokeWidth={2} />
                ) : (
                  <polygon points={d} fill="rgba(56,189,248,0.15)" stroke="#38bdf8" strokeWidth={2} />
                )}
                {label(s2[0][0], s2[0][1], x.name, "#38bdf8")}
              </g>
            );
          })}
          {pts.length > 0 && (() => {
            const corners = (mode === "ttc" ? pts.slice(0, 4) : pts).map(toScreen);
            const d = corners.map((p) => p.join(",")).join(" ");
            const closed = (mode === "zone" || mode === "ttc") && corners.length >= 3;
            const lanePts = mode === "ttc" ? pts.slice(4).map(toScreen) : [];
            return (
              <g>
                {closed ? (
                  <polygon points={d} fill="rgba(237,105,38,0.2)" stroke="#ED6926" strokeWidth={2.5} />
                ) : (
                  <polyline points={d} fill="none" stroke="#ED6926" strokeWidth={2.5} />
                )}
                {lanePts.map((p, i) =>
                  i % 2 === 1 ? (
                    <line key={`nl-${i}`} x1={lanePts[i - 1][0]} y1={lanePts[i - 1][1]} x2={p[0]} y2={p[1]} stroke="#facc15" strokeWidth={2.5} strokeDasharray="6 5" />
                  ) : null
                )}
                {pts.map(toScreen).map((p, i) => (
                  <g key={i}>
                    <circle cx={p[0]} cy={p[1]} r={5} fill={i >= 4 && mode === "ttc" ? "#facc15" : "#ED6926"} stroke="#fff" strokeWidth={2} />
                    {mode === "ttc" && i < 4 && label(p[0], p[1], String(i + 1), "#fff")}
                  </g>
                ))}
              </g>
            );
          })()}
        </svg>
      )}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "absolute", top: 8, right: 10, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, maxWidth: "75%" }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {!active ? (
            <button type="button" style={btn(false)} onClick={() => setActive(true)} disabled={!tunnelUrl} title={tunnelUrl ? "" : "This board has no tunnel"}>
              Edit lines / zones
            </button>
          ) : (
            <>
              <button type="button" style={btn(mode === "line")} onClick={() => startMode("line")}>Create Line</button>
              <button type="button" style={btn(mode === "zone")} onClick={() => startMode("zone")}>Create Zone</button>
              <button type="button" style={btn(mode === "ttc")} onClick={() => startMode("ttc")}>TTC Zone + lanes</button>
              <button type="button" style={btn(false)} onClick={() => setActive(false)}>Done</button>
            </>
          )}
        </div>
        {active && (
          <div style={{ background: "rgba(255,255,255,.96)", borderRadius: 6, padding: "6px 8px", fontSize: 12, color: "#1f2937", boxShadow: "0 2px 8px rgba(0,0,0,.2)", display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ color: "#4b5563" }}>
              Lines last updated: {updated ? `${updated}${config?.updated_by ? ` · ${config.updated_by}` : ""}` : "—"}
            </span>
            <span style={{ color: "#4b5563" }}>
              TTC last updated: {ttcUpdated ? `${ttcUpdated}${ttc?.updated_by ? ` · ${ttc.updated_by}` : ""}` : "—"}
            </span>
            {mode && (
              <>
                <span>
                  {hintFor(mode, pts.length)}
                  {pts.length > 0 && (
                    <button type="button" onClick={() => setPts((p) => p.slice(0, -1))} style={{ marginLeft: 8, border: "none", background: "none", color: "#ED6926", cursor: "pointer", fontSize: 12 }}>
                      Undo
                    </button>
                  )}
                  {pts.length > 0 && (
                    <button type="button" onClick={() => setPts([])} style={{ marginLeft: 8, border: "none", background: "none", color: "#ED6926", cursor: "pointer", fontSize: 12 }}>
                      Clear
                    </button>
                  )}
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {(
                    <>
                      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" style={{ width: 100, height: 26, fontSize: 12, padding: "0 6px", border: "1px solid #d1d5db", borderRadius: 4 }} />
                      <input
                        value={dist}
                        onChange={(e) => setDist(e.target.value)}
                        placeholder={mode === "ttc" ? "Entry→exit m" : "Distance m"}
                        title={mode === "ttc" ? "Distance on the road between the entry and the exit line, in metres" : undefined}
                        type="number"
                        style={{ width: 90, height: 26, fontSize: 12, padding: "0 6px", border: "1px solid #d1d5db", borderRadius: 4 }}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={save}
                    disabled={busy || !ready || !name.trim()}
                    style={{ ...btn(true), opacity: busy || !ready || !name.trim() ? 0.5 : 1 }}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            {status && <span style={{ color: status.startsWith("Could not") || status.startsWith("This board cannot") ? "#dc2626" : "#047857", maxWidth: 380 }}>{status}</span>}
          </div>
        )}
      </div>
    </>
  );
};

export default LineZoneEditor;
